import type { LanguageCode } from "../languages/language-codes";
import { Button } from "@acdc2/ui/components/button";
import { Input } from "@acdc2/ui/components/input";
import { mdiCheck, mdiClose, mdiDelete, mdiPencil } from "@mdi/js";
import { Icon } from "@mdi/react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { MetadataTitleLanguageCombobox } from "./MetadataTitleLanguageCombobox";
import { Form, FormMessage } from "@acdc2/ui/components/access-form";
import { Control, Field } from "@radix-ui/react-form";

type Props = {
  defaultLanguage?: LanguageCode;
  defaultTitle?: string;
  onSave: (newLanguage: string, newTitle: string) => void;
  onDelete: () => void;
};

export function MetadataTitleInput({
  defaultLanguage,
  defaultTitle,
  onSave,
  onDelete,
}: Props) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(!defaultLanguage && !defaultTitle);
  const [newLanguage, setNewLanguage] = useState(defaultLanguage);
  const [newTitle, setNewTitle] = useState(defaultTitle);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!newLanguage || !newTitle) return;
    onSave(newLanguage, newTitle);
    setIsEditing(false);
  };

  const onReset = (event: React.FormEvent) => {
    event.preventDefault();
    setNewLanguage(defaultLanguage);
    setNewTitle(defaultTitle);
    setIsEditing(false);
  };

  return (
    <Form onSubmit={onSubmit} onReset={onReset}>
      <div className="flex flex-row gap-2">
        <Field name="language">
          <Control asChild>
            <input type="text" value={newLanguage} required hidden />
          </Control>
          <MetadataTitleLanguageCombobox
            value={newLanguage}
            onValueChange={setNewLanguage}
            disabled={!isEditing}
          />
        </Field>

        <Field name="title" className="flex-1">
          <Control asChild>
            <Input
              type="text"
              value={newTitle}
              onChange={(event) => setNewTitle(event.target.value)}
              placeholder={t("MetadataTitleInput.placeholders.title")}
              disabled={!isEditing}
              required
            />
          </Control>
        </Field>

        <div className="flex flex-row gap-2">
          {isEditing ? (
            <Fragment key="editing-buttons">
              <Button type="submit" size="icon" variant="outline">
                <Icon path={mdiCheck} size="1.25rem" />
              </Button>
              <Button type="reset" size="icon" variant="outline">
                <Icon path={mdiClose} size="1.25rem" />
              </Button>
            </Fragment>
          ) : (
            <Fragment key="view-buttons">
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={() => setIsEditing(true)}
              >
                <Icon path={mdiPencil} size="1.25rem" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={() => onDelete()}
              >
                <Icon path={mdiDelete} size="1.25rem" />
              </Button>
            </Fragment>
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <FormMessage name="language" match="valueMissing" className="mt-1">
          {t("MetadataTitleInput.errors.languageMissing")}
        </FormMessage>
        <FormMessage name="title" match="valueMissing" className="mt-1">
          {t("MetadataTitleInput.errors.titleMissing")}
        </FormMessage>
      </div>
    </Form>
  );
}
